import { QueryClient } from '@tanstack/solid-query';
import {
  batchGet as fetchBatchGet,
  getPSet as fetchPSet,
  listPsetsByLink as fetchListPsetsByLink,
  updatePSet as patchPSet,
  type BatchGetData,
  type BatchGetResponse,
  type DeletePSetData,
  type GetPSetData,
  type GetPSetResponse,
  type ListDefinitionsData,
  type ListPSetsByDefData,
  type ListPSetsByLibData,
  type ListPsetsByLinkData,
  type ListPsetsByLinkResponse,
  type MeData,
  type Options,
  type UpdatePSetData,
  type UpdatePSetResponse,
  type UpsertDefinitionData,
} from './generated';
import {
  deletePSetMutation as createDeletePSetMutation,
  getPSetOptions as createGetPSetOptions,
  getPSetQueryKey,
  listDefinitionsOptions as createListDefinitionsOptions,
  listDefinitionsQueryKey,
  listPSetsByDefOptions as createListPSetsByDefOptions,
  listPSetsByDefQueryKey,
  listPSetsByLibOptions as createListPSetsByLibOptions,
  listPSetsByLibQueryKey,
  listPsetsByLinkOptions as createListPsetsByLinkOptions,
  listPsetsByLinkQueryKey,
  updatePSetMutation as createUpdatePSetMutation,
  upsertDefinitionMutation as createUpsertDefinitionMutation,
  meOptions as createMeOptions,
} from './generated/@tanstack/solid-query.gen';
import type { Client as PsetClient } from './generated/client/types.gen';
import {
  FAVORITES_SCHEMA_VERSION,
  getFavoritePropertiesPath,
} from './helpers/favorite-property-keys';

const HTTP_NOT_FOUND = 404;

export interface GetFavoritePropertyKeysParams {
  projectId: string;
  userId: string;
  signal?: AbortSignal;
}

export interface UpdateFavoritePropertyKeysParams {
  projectId: string;
  userId: string;
  favoritePropertyKeys: readonly string[];
}

export class TcPsetApiService {
  constructor(
    private readonly client: PsetClient,
    private readonly queryClient: QueryClient
  ) {}

  public getClient(): PsetClient {
    return this.client;
  }

  public getMeOptions(
    options?: Omit<Options<MeData>, 'client'>
  ): ReturnType<typeof createMeOptions> {
    return createMeOptions({
      ...options,
      client: this.client,
    });
  }

  public listDefinitionsOptions(
    options: Omit<Options<ListDefinitionsData>, 'client'>
  ): ReturnType<typeof createListDefinitionsOptions> {
    return createListDefinitionsOptions({
      ...options,
      client: this.client,
    });
  }

  public listPSetsByLibOptions(
    options: Omit<Options<ListPSetsByLibData>, 'client'>
  ): ReturnType<typeof createListPSetsByLibOptions> {
    return createListPSetsByLibOptions({
      ...options,
      client: this.client,
    });
  }

  public listPSetsByDefOptions(
    options: Omit<Options<ListPSetsByDefData>, 'client'>
  ): ReturnType<typeof createListPSetsByDefOptions> {
    return createListPSetsByDefOptions({
      ...options,
      client: this.client,
    });
  }

  public listPsetsByLinkOptions(
    options: Omit<Options<ListPsetsByLinkData>, 'client'>
  ): ReturnType<typeof createListPsetsByLinkOptions> {
    return createListPsetsByLinkOptions({
      ...options,
      client: this.client,
    });
  }

  public async listPsetsByLink(
    options: Omit<Options<ListPsetsByLinkData>, 'client'>
  ): Promise<ListPsetsByLinkResponse> {
    const { data } = await fetchListPsetsByLink({
      ...options,
      client: this.client,
      throwOnError: true,
    });
    return data;
  }

  public async batchGet(
    options: Omit<Options<BatchGetData>, 'client'>
  ): Promise<BatchGetResponse> {
    const { data } = await fetchBatchGet({
      ...options,
      client: this.client,
      throwOnError: true,
    });
    return data;
  }

  public getPSetOptions(
    options: Omit<Options<GetPSetData>, 'client'>
  ): ReturnType<typeof createGetPSetOptions> {
    return createGetPSetOptions({
      ...options,
      client: this.client,
    });
  }

  public async getPSet(
    options: Omit<Options<GetPSetData>, 'client'>
  ): Promise<GetPSetResponse> {
    const { data } = await fetchPSet({
      ...options,
      client: this.client,
      throwOnError: true,
    });
    return data;
  }

  public updatePSetMutation(
    options?: Partial<Omit<Options<UpdatePSetData>, 'client'>>
  ): ReturnType<typeof createUpdatePSetMutation> {
    return createUpdatePSetMutation({
      ...options,
      client: this.client,
    });
  }

  public async updatePSet(
    options: Omit<Options<UpdatePSetData>, 'client'>
  ): Promise<UpdatePSetResponse> {
    const { data } = await patchPSet({
      ...options,
      client: this.client,
      throwOnError: true,
    });
    return data;
  }

  public async getFavoritePropertyKeys(
    params: GetFavoritePropertyKeysParams
  ): Promise<string[]> {
    const result = await fetchPSet({
      path: getFavoritePropertiesPath(params.userId, params.projectId),
      signal: params.signal,
      client: this.client,
      throwOnError: false,
    });

    if (result.response?.status === HTTP_NOT_FOUND) {
      return [];
    }

    if (result.error !== undefined || result.data === undefined) {
      throw result.error ?? new Error('Failed to load favorite property keys');
    }

    const favoriteProps = result.data.props['favProps'];
    return Array.isArray(favoriteProps)
      ? favoriteProps.filter((key): key is string => typeof key === 'string')
      : [];
  }

  public async updateFavoritePropertyKeys(
    params: UpdateFavoritePropertyKeysParams
  ): Promise<void> {
    await this.updatePSet({
      path: getFavoritePropertiesPath(params.userId, params.projectId),
      body: {
        schemaV: FAVORITES_SCHEMA_VERSION,
        props: {
          favProps: [...params.favoritePropertyKeys],
        },
      },
    });
  }

  public deletePSetMutation(
    options?: Partial<Omit<Options<DeletePSetData>, 'client'>>
  ): ReturnType<typeof createDeletePSetMutation> {
    return createDeletePSetMutation({
      ...options,
      client: this.client,
    });
  }

  public upsertDefinitionMutation(
    options?: Partial<Omit<Options<UpsertDefinitionData>, 'client'>>
  ): ReturnType<typeof createUpsertDefinitionMutation> {
    return createUpsertDefinitionMutation({
      ...options,
      client: this.client,
    });
  }

  public async invalidateListDefinitions(
    options: Omit<Options<ListDefinitionsData>, 'client'>
  ): Promise<void> {
    await this.queryClient.invalidateQueries({
      queryKey: listDefinitionsQueryKey({
        ...options,
        client: this.client,
      }),
    });
  }

  public async invalidatePSetsByLib(
    options: Omit<Options<ListPSetsByLibData>, 'client'>
  ): Promise<void> {
    await this.queryClient.invalidateQueries({
      queryKey: listPSetsByLibQueryKey({
        ...options,
        client: this.client,
      }),
    });
  }

  public async invalidatePSetsByDef(
    options: Omit<Options<ListPSetsByDefData>, 'client'>
  ): Promise<void> {
    await this.queryClient.invalidateQueries({
      queryKey: listPSetsByDefQueryKey({
        ...options,
        client: this.client,
      }),
    });
  }

  public async invalidatePsetsByLink(
    options: Omit<Options<ListPsetsByLinkData>, 'client'>
  ): Promise<void> {
    await this.queryClient.invalidateQueries({
      queryKey: listPsetsByLinkQueryKey({
        ...options,
        client: this.client,
      }),
    });
  }

  public async invalidatePSet(
    options: Omit<Options<GetPSetData>, 'client'>
  ): Promise<void> {
    await this.queryClient.invalidateQueries({
      queryKey: getPSetQueryKey({
        ...options,
        client: this.client,
      }),
    });
  }
}
